import React, { useState, useRef, useLayoutEffect } from 'react';
import { useTheme } from '../ThemeContext';
import StarRating from './StarRating';

// Shared 5a (Home) / 5b (Individual Report) template. Redesigned 2026-07-23 from the
// "Team KPI Dashboard" Claude-design handoff (design_handoff_team_kpi_dashboard/) — a fixed
// pentagon "honeycomb" of Apple-Watch-style circular-progress-ring bubbles around a center
// crest/photo badge, no radar/spider chart (explicitly cut in that design in favor of the
// rings themselves carrying the same information). Bubble size/position is now the design's
// fixed per-pillar layout, not the old weight-proportional formula — Physical is uniquely
// the largest/top-center "hero" bubble regardless of its exact weight value.
//
// Bubble color: night theme uses #22d3ee (light cyan) for the ring/accent; day theme uses the
// darker #0891b2 for contrast against a light background — both pulled from the handoff's
// design tokens, and switched via the app's existing ThemeContext/data-theme mechanism
// (src/ThemeContext.js, src/index.css), not a new one-off toggle.
//
// 5c (the player PWA variant) is intentionally NOT built on this component. As of the
// "Day spine v2" redesign (2026-08-19, src/pages/player/PlayerToday.js) the player Today
// screen no longer surfaces a Physical/Technical/Medical pillar-scores dial at all — that
// design replaced it with an assistant-style day brief + 7-day timeline. The objective/
// subjective split (players never see Tactical/Identity/overall) still applies to whatever
// pillar view the player app uses next; it's enforced by which component exists, not a
// prop flag here.

// Kept as a named export — IndividualReport.js's comparison-mode radar/table (which doesn't
// use the honeycomb layout at all) still needs pillar display labels.
export const PILLAR_LABELS = {
  physical: 'Physical', availability: 'Availability', technical: 'Technical',
  identity: 'Identity', tactical: 'Tactical',
};

const PILLAR_LAYOUT = {
  physical:     { label: 'Physical',     size: 150, style: { left: '50%', top: 6, transform: 'translateX(-50%)' } },
  availability: { label: 'Availability', size: 120, style: { left: 70, top: 180 } },
  technical:    { label: 'Technical',    size: 120, style: { right: 70, top: 180 } },
  identity:     { label: 'Identity',     size: 110, style: { left: 110, top: 400 } },
  tactical:     { label: 'Tactical',     size: 110, style: { right: 110, top: 400 } },
};
const HONEYCOMB_WIDTH = 640;
const HONEYCOMB_HEIGHT = 560;

const PERIODS = [
  { value: 'week', label: 'Week' },
  { value: '28day', label: '28-Day' },
  { value: 'preseason', label: 'Pre-season' },
  { value: 'season', label: 'Season' },
];

// The honeycomb's bubbles/badge are laid out with fixed pixel offsets against a 640x560
// design canvas (see PILLAR_LAYOUT / CenterBadge above). Rather than trying to make every
// offset percentage-based (fragile — the pentagon's visual balance depends on the exact
// px relationships from the design handoff), we measure the actual space available at
// render time and scale the whole canvas down as one rigid unit via CSS transform. That
// keeps every bubble's position/size/text proportional to every other one at any container
// width, so nothing overlaps — and factoring remaining viewport height into the same scale
// (not just width) means the cluster shrinks to fit above the fold instead of pushing
// content below it off-screen, per explicit feedback 2026-07-22 ("scale everything so
// nothing overlaps and I don't need to scroll down"). `bottomReserve` lets each page tell
// this component how much space its own chrome below the honeycomb needs (e.g. Home's
// action-button row) since that isn't part of this shared component.
function useHoneycombScale(bottomReserve) {
  const stageRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = stageRef.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;

    const recompute = () => {
      const availWidth = parent.clientWidth;
      const top = el.getBoundingClientRect().top;
      const availHeight = window.innerHeight - top - bottomReserve;
      const next = Math.min(1, availWidth / HONEYCOMB_WIDTH, Math.max(200, availHeight) / HONEYCOMB_HEIGHT);
      setScale(next > 0 ? next : 1);
    };

    recompute();
    window.addEventListener('resize', recompute);
    const ro = new ResizeObserver(recompute);
    ro.observe(parent);
    return () => { window.removeEventListener('resize', recompute); ro.disconnect(); };
  }, [bottomReserve]);

  return { stageRef, scale };
}

// Apple-Watch-style activity ring: an SVG circle outline padded 12px outside the bubble,
// rotated -90deg so the fill starts at 12 o'clock, filled clockwise proportional to value.
// The ring itself clamps visually at 100% (a maxed-out ring) even though Physical's own
// number can read past 100 (deliberately uncapped, per Stage 3) — the numeric label below
// always shows the true unclamped value, only the ring fill is clamped.
function RingBubble({ pillarKey, value, onClick, isDay }) {
  const { label, size } = PILLAR_LAYOUT[pillarKey];
  const hasData = value != null;
  const pad = 12, strokeWidth = 6;
  const outerSize = size + pad * 2;
  const c = outerSize / 2;
  const r = c - strokeWidth / 2;
  const circumference = 2 * Math.PI * r;
  const clampedPct = hasData ? Math.max(0, Math.min(100, value)) : 0;
  const arcLen = circumference * (clampedPct / 100);
  const accent = isDay ? '#0891b2' : '#22d3ee';
  const track = isDay ? 'rgba(20,24,28,0.12)' : 'rgba(255,255,255,0.10)';
  const clickable = typeof onClick === 'function';

  const bubbleBg = hasData
    ? (isDay ? 'linear-gradient(160deg, rgba(255,255,255,0.9), rgba(255,255,255,0.55))'
             : 'linear-gradient(160deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))')
    : (isDay ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)');
  const bubbleBorder = hasData
    ? `1.5px solid ${isDay ? 'rgba(8,145,178,0.35)' : 'rgba(34,211,238,0.35)'}`
    : `1.5px dashed ${isDay ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.18)'}`;
  const bubbleShadow = hasData
    ? (isDay ? '0 8px 20px rgba(0,0,0,0.08), 0 0 24px rgba(34,211,238,0.14)'
             : '0 8px 24px rgba(0,0,0,0.35), 0 0 30px rgba(34,211,238,0.12)')
    : 'none';
  const labelColor = isDay ? 'rgba(20,24,28,0.55)' : 'rgba(255,255,255,0.5)';
  const valueColor = hasData
    ? (isDay ? '#1c2024' : '#fff')
    : (isDay ? 'rgba(20,24,28,0.35)' : 'rgba(255,255,255,0.4)');

  // No `disabled` attribute for the non-clickable case — see the earlier fix note: it pulls
  // in the browser's default dimmed styling, which reads as "broken," not "not built yet."
  return (
    <div style={{ position: 'absolute', ...PILLAR_LAYOUT[pillarKey].style }}>
      <button
        type="button"
        onClick={clickable ? onClick : undefined}
        title={clickable ? `Go to ${label}` : label}
        style={{ position: 'relative', width: size, height: size, border: 'none', background: 'transparent', padding: 0, cursor: clickable ? 'pointer' : 'default' }}
      >
        <svg width={outerSize} height={outerSize} viewBox={`0 0 ${outerSize} ${outerSize}`}
          style={{ position: 'absolute', left: -pad, top: -pad, transform: 'rotate(-90deg)' }}>
          <circle cx={c} cy={c} r={r} fill="none" stroke={track} strokeWidth={strokeWidth} />
          {hasData && (
            <circle cx={c} cy={c} r={r} fill="none" stroke={accent} strokeWidth={strokeWidth}
              strokeLinecap="round" strokeDasharray={`${arcLen} ${circumference}`} />
          )}
        </svg>
        <div style={{
          width: size, height: size, borderRadius: '50%',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
          background: bubbleBg, border: bubbleBorder, boxShadow: bubbleShadow, boxSizing: 'border-box',
        }}>
          <div style={{ fontSize: size >= 140 ? 11 : 10, fontWeight: 700, letterSpacing: '1.2px', color: labelColor, textTransform: 'uppercase' }}>
            {label}
          </div>
          <div style={{ fontSize: hasData ? (size >= 140 ? 38 : size >= 120 ? 30 : 26) : (size >= 140 ? 18 : 15), fontWeight: hasData ? 800 : 700, color: valueColor, marginTop: hasData ? 4 : 6 }}>
            {hasData ? Math.round(value) : 'no data'}
          </div>
        </div>
      </button>
    </div>
  );
}

// Tracks its own load-error state so a broken/missing photo_url falls back to the initial
// letter instead of leaving an empty circle. `fill=true` (player photo) covers the whole
// badge edge-to-edge, clipped to the circle (needs the crop); `fill=false` (club crest)
// renders larger than the badge and is allowed to overflow it — a pinned-on badge look, the
// crest sitting in front of the ring rather than boxed inside it (2026-07-23). Child elements
// paint after (in front of) their parent's own background/border by default, so simply not
// clipping the overflow is enough to put the crest in front of the circle without any
// z-index — no need for one here.
function CenterBadge({ imageUrl, initial, fill, isDay }) {
  const [errored, setErrored] = useState(false);
  const showImg = imageUrl && !errored;
  return (
    <div style={{
      position: 'absolute', left: '50%', top: 230, transform: 'translateX(-50%)',
      width: 170, height: 170, borderRadius: '50%', overflow: fill ? 'hidden' : 'visible',
      background: isDay
        ? 'radial-gradient(circle at 50% 40%, rgba(34,211,238,0.18), rgba(255,255,255,0.9) 70%)'
        : 'radial-gradient(circle at 50% 40%, rgba(34,211,238,0.22), rgba(20,22,26,0.6) 70%)',
      border: `2px solid ${isDay ? 'rgba(8,145,178,0.4)' : 'rgba(34,211,238,0.4)'}`,
      boxShadow: isDay
        ? '0 0 50px rgba(34,211,238,0.20), inset 0 0 20px rgba(0,0,0,0.06)'
        : '0 0 50px rgba(34,211,238,0.25), inset 0 0 30px rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {showImg ? (
        <img src={imageUrl} alt="" onError={() => setErrored(true)}
          style={fill
            ? { width: '100%', height: '100%', objectFit: 'cover' }
            : { width: 150, height: 'auto', filter: `drop-shadow(0 6px 14px rgba(0,0,0,${isDay ? 0.18 : 0.5}))` }} />
      ) : (
        <span style={{ fontSize: 44, fontWeight: 800, color: isDay ? 'rgba(20,24,28,0.4)' : 'rgba(255,255,255,0.5)' }}>{initial}</span>
      )}
    </div>
  );
}

export default function PillarBubbleSpider({
  centerImageUrl, centerInitial, centerImageFill = false, title, subtitle,
  stars, pillars, period, onPeriodChange, bubbleLinks = {}, bottomReserve = 24,
}) {
  const { theme } = useTheme();
  const isDay = theme === 'day';
  const accent = isDay ? '#0891b2' : '#22d3ee';
  const pillarKeys = ['physical', 'availability', 'technical', 'identity', 'tactical'];
  const { stageRef, scale } = useHoneycombScale(bottomReserve);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
        <div style={{ display: 'inline-flex', gap: 6, background: isDay ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.06)', border: `1px solid ${isDay ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.10)'}`, padding: 5, borderRadius: 999, backdropFilter: 'blur(10px)' }}>
          {PERIODS.map(p => {
            const active = period === p.value;
            return (
              <button key={p.value} onClick={() => onPeriodChange(p.value)}
                style={{
                  padding: '9px 22px', borderRadius: 999, fontSize: 14, fontWeight: 700, letterSpacing: '0.2px',
                  cursor: 'pointer', border: 'none',
                  color: active ? '#04141a' : (isDay ? 'rgba(20,24,28,0.55)' : 'rgba(255,255,255,0.65)'),
                  background: active ? 'linear-gradient(135deg,#22d3ee,#0891b2)' : 'transparent',
                  boxShadow: active ? '0 4px 18px rgba(34,211,238,0.35)' : 'none',
                }}>
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 26 }}>
        <div style={{ fontFamily: "'Archivo Black', 'Arial Black', Impact, sans-serif", fontSize: 34, color: isDay ? '#1c2024' : '#fff', letterSpacing: '0.5px', textTransform: 'uppercase', textAlign: 'center' }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 14, color: isDay ? 'rgba(20,24,28,0.45)' : 'rgba(255,255,255,0.45)', marginTop: 4, letterSpacing: '1.5px', textTransform: 'uppercase' }}>
            {subtitle}
          </div>
        )}
        <div style={{ marginTop: 14 }}>
          {stars == null ? (
            <span style={{ fontSize: 14, color: isDay ? 'rgba(20,24,28,0.4)' : 'rgba(255,255,255,0.4)' }}>No star rating yet — no data</span>
          ) : (
            <StarRating value={stars} size={26} color={accent} emptyColor={isDay ? 'rgba(20,24,28,0.15)' : 'rgba(255,255,255,0.2)'} gap={3} />
          )}
        </div>
      </div>

      <div ref={stageRef} style={{ position: 'relative', width: HONEYCOMB_WIDTH * scale, height: HONEYCOMB_HEIGHT * scale, margin: '24px auto 0' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: HONEYCOMB_WIDTH, height: HONEYCOMB_HEIGHT, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          <CenterBadge imageUrl={centerImageUrl} initial={centerInitial} fill={centerImageFill} isDay={isDay} />
          {pillarKeys.map(k => (
            <RingBubble key={k} pillarKey={k} value={pillars[k]} onClick={bubbleLinks[k]} isDay={isDay} />
          ))}
        </div>
      </div>
    </div>
  );
}
